/* -----------------Globals--------------- */
import { useCallback, useEffect } from "react";

/* -----------------UI--------------- */
import { Box, FAB, ProfileIcon, useDevice } from "@memorang/ui";

import { useExamContext } from "@features/exam";
/* -----------------Helpers & Hooks--------------- */
import { isIOS, isWeb } from "@helpers/platform";
import { getUserPrompt } from "../components/QASearch/helpers";
import { useAilaStore } from "../hooks/useAilaStore";
import useSessionStore from "../hooks/useSessionStore";

import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { Portal } from "react-native-paper";
/* -----------------Child components--------------- */
import PostAnswerModal from "../components/postanswer/PostAnswerModal";

/* -----------------Types--------------- */
import type { OriginalQuestionData } from "@memorang/features";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
	sessionMaxWidth,
} from "../constants";
import { getParentStem, replaceBrackets } from "../hooks/useAIFeedback";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";

import i18n from "@helpers/lib/i18n";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { getTotalScoreForAiItem } from "../helpers/aiFeedback";

const StudySessionFeedbackContainer = ({
	hasParentStem,
}: {
	hasParentStem?: boolean;
}) => {
	const { index } = useLocalSearchParams<{
		index: string;
		prev: string;
	}>();

	const { isMobile } = useDevice();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const sessionItems = useSessionStore((state) => state.sessionItems);
	const answers = useSessionStore((state) => state.answers);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentSessionItems = sessionItems;
	const currentItem = currentSessionItems[currentItemIndex];
	const aiFeedback = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.explanation,
	);

	const { content, metadata, stimulus } = currentItem;
	const choices = content?.choices ?? [];
	const stem = content?.stem!;
	const type = metadata?.type!;
	const itemType = MAPPED_ITEM_TYPES[type];
	const stimulusType = stimulus?.metadata?.type;

	const isAiItemType = AI_SUBMITTABLE_VARIANTS.includes(itemType);
	const isStreaming = useAIFeedbackStore((store) => store.streaming);
	const topics = metadata?.topics!;
	const itemId = currentItem.id!;

	const currentAnswer = answers?.get(currentItem.id!);

	const currentUserResponse = userResponse?.[itemId] as string | undefined;
	const numberOfWords =
		type === "ACADEMIC_DISCUSSION_RESPONSE" ||
		type === "INTEGRATED_WRITING_RESPONSE"
			? currentUserResponse?.trim().split(/\s+/).length || 0
			: undefined;

	const { bottom } = useSafeAreaInsets();
	const isAssistantVisible = useAilaStore((state) => state.showDrawer);
	const updateIsAssistantVisible = useAilaStore(
		(state) => state.updateShowDrawer,
	);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);
	const {
		currentExam: { ailaName, icon },
	} = useDemoExamStore();
	const router = useRouter();

	const handleAssistantFabPress = () => {
		updateIsAssistantVisible(true);
		if (!isWeb) {
			router.push(`/aila/${currentItem.id}`);
		}
	};

	useFocusEffect(
		useCallback(() => {
			if (!isWeb) {
				updateIsAssistantVisible(false);
				updateSelectedQuery("");
			}
		}, [updateIsAssistantVisible, updateSelectedQuery]),
	);

	useEffect(() => {
		if (isSummativeTest && inReviewMode) {
			updateIsAssistantVisible(true);
		}
	}, [isSummativeTest, inReviewMode, updateIsAssistantVisible]);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	// In review, show the explanation for every graded question - including ones
	// the learner skipped, which have no `answers` entry (gradeItemsForReview only
	// seeds itemStates). Gate on choices so non-question items (lessons, sims,
	// module landing) don't render an empty explanation. Outside review, keep the
	// answered-gated behavior.
	const shouldRenderPostAnswerModal = inReviewMode
		? choices.length > 0
		: isSummativeTest
			? false
			: currentAnswer != null;

	const _shouldRenderFollowUps =
		!!shouldRenderPostAnswerModal && isWeb && !(isAiItemType && isStreaming);

	const confidence = currentAnswer?.confidence;
	const timeTaken = currentAnswer?.timeTaken;
	const correct = currentAnswer?.correct;
	const maximumScore =
		currentItem.response?.maximumScore ?? getTotalScoreForAiItem(itemType);
	const subskillsSchema = currentItem.response?.subskillsSchema;

	const correctChoices = choices?.filter(({ isCorrect }) => isCorrect);

	const selectedChoice = currentAnswer?.selectedChoiceIds?.map((choiceId) => {
		const choice = choices.find((choice) => choice.id === choiceId);
		return {
			isCorrect: choice?.isCorrect,
			stem: choice?.stem,
			text: choice?.text,
		};
	});
	const data = {
		stem,
		explanation: isAiItemType
			? aiFeedback
			: i18n.language === "zh-CN" && correctChoices[0]?.explanation_mandarin
				? correctChoices[0]?.explanation_mandarin
				: (correctChoices[0]?.explanation ?? ""),
		choices,
		topics,
	};
	const userPrompt = replaceBrackets(
		`${getParentStem(currentItem)}\n\n${getUserPrompt(data, examName)}`,
	);
	const originalQuestionData: OriginalQuestionData = {
		stem: data.stem,
		explanation: data?.explanation ?? "",
		topics,
		choices:
			data.choices.map((choice) => ({
				text: choice.stem,
				isCorrect: choice.isCorrect,
			})) ?? [],
		selectedChoice: selectedChoice ?? [],
	};

	const fabBottomPosition = 80 + 24 + (isWeb ? 16 : 0);

	const isAiLAFabVisible = false;

	return (
		<Box
			maxWidth={
				isSummativeTest ? sessionMaxWidth.summative : sessionMaxWidth.formative
			}
			alignSelf={
				hasParentStem ? undefined : isSummativeTest ? "flex-start" : "center"
			}
			paddingHorizontal={isMobile ? 16 : 0}
		>
			{isAiLAFabVisible && (
				<Portal>
					<FAB
						icon={(_props) => <ProfileIcon uri={icon} />}
						label={ailaName}
						style={{
							position: "absolute",
							bottom: fabBottomPosition + (isIOS ? bottom - 12 : 0),
							right: 24,
						}}
						onPress={handleAssistantFabPress}
					/>
				</Portal>
			)}
			{shouldRenderPostAnswerModal && (
				<PostAnswerModal
					choices={choices}
					timeTaken={timeTaken ?? 0}
					confidence={confidence}
					showExplanation
					correct={correct}
					tags={topics}
					currentQuestionAnswered
					currentItemIndex={currentItemIndex}
					itemId={currentItem.id!}
					userPrompt={userPrompt}
					originalQuestionData={originalQuestionData}
					itemType={itemType}
					numberOfWords={numberOfWords}
					maximumScore={maximumScore}
					originalItemType={type}
					stimulusType={stimulusType}
					subskillsSchema={subskillsSchema}
				/>
			)}
			{/* <Box paddingVertical={16} gap={16}>
				{(!isSummativeTest || inReviewMode) && (
					<QAChipsWrapper
						itemId={currentItem.id!}
						userPrompt={userPrompt}
						shouldRender={shouldRenderFollowUps}
						originalQuestionData={originalQuestionData}
						loading={isAiItemType && isStreaming}
					/>
				)}
			</Box> */}
		</Box>
	);
};

export default StudySessionFeedbackContainer;
